/**
 * Menu theo role – POC và triển khai thật dùng chung.
 * Đương sự (user/student/litigant) | Thư ký (secretary) | Thẩm phán (judge) | Chánh án (admin/chief_judge)
 */
import type { ReactNode } from 'react'
import {
  AddCircle,
  UploadFile,
  Dashboard,
  Assessment,
  HowToReg,
  AutoAwesome,
  Gavel,
  RateReview,
} from '@mui/icons-material'

export interface MenuSubItem {
  id: string
  text: string
  path: string
}

export interface MenuItem {
  text: string
  icon: ReactNode
  path: string
  roles?: string[] // undefined = mọi role đều thấy
  /** Sub-menu (navy tone) khi vào đúng path. */
  children?: MenuSubItem[]
  /** Nhóm Smart Legal Tech: luôn expand khi pathname bắt đầu /workspace/ai- */
  isAIGroup?: boolean
}

export const menuItems: MenuItem[] = [
  { text: 'Bảng điều khiển', icon: <Dashboard />, path: '/workspace', roles: ['secretary', 'judge', 'admin', 'chief_judge'] },
  { text: 'Vụ án của tôi', icon: <Dashboard />, path: '/cases', roles: ['user', 'student', 'litigant'] },
  { text: 'Nộp đơn khởi kiện', icon: <AddCircle />, path: '/cases/new', roles: ['user', 'student', 'litigant'] },
  { text: 'Giao nộp chứng cứ', icon: <UploadFile />, path: '/cases/evidence', roles: [] },
  { text: 'Văn bản chờ duyệt/Ký', icon: <RateReview />, path: '/workspace/documents-pending', roles: ['admin', 'chief_judge'] },
  { text: 'Quyết định thẩm quyền', icon: <Gavel />, path: '/workspace/preliminary-chief', roles: ['admin', 'chief_judge'] },
  { text: 'Phân công án', icon: <HowToReg />, path: '/workspace/assign', roles: ['admin', 'chief_judge'] },
  {
    text: 'Smart Legal Tech',
    icon: <AutoAwesome />,
    path: '/workspace/ai-court-secretary',
    roles: ['secretary', 'judge', 'admin', 'chief_judge'],
    isAIGroup: true,
    children: [
      { id: 'ai-court-secretary', text: 'Trợ lý webinar', path: '/workspace/ai-court-secretary' },
      { id: 'ai-draft', text: 'Trợ lý văn bản', path: '/workspace/ai-draft' },
      { id: 'ai-research', text: 'Trợ lý pháp luật', path: '/workspace/ai-research' },
    ],
  },
]

/** Menu riêng cho Chánh án: Bảng điều khiển, Quyết định thẩm quyền, Văn bản chờ ký, Phân công án, Báo cáo, Smart Legal Tech. */
const CHIEF_JUDGE_PATHS = [
  '/workspace',
  // Các chức năng quản trị được gộp thành section ngay trong Bảng điều khiển
  '/workspace/ai-court-secretary',
  '/workspace/ai-draft',
  '/workspace/ai-research',
]

export function getMenuItemsForRole(role: string): MenuItem[] {
  const isChiefJudge = role === 'admin' || role === 'chief_judge'
  const isLitigant = ['user', 'litigant', 'student'].includes(role)
  if (isChiefJudge) {
    return menuItems.filter(
      (item) =>
        CHIEF_JUDGE_PATHS.includes(item.path) ||
        (item.isAIGroup && item.children?.some((c) => CHIEF_JUDGE_PATHS.includes(c.path)))
    )
  }
  return menuItems.filter((item) => !item.roles || item.roles.includes(role))
}
